// The three steps before a post exists: the moment, the photos, the shape. Nothing here costs
// a credit; Make it at the end spends what a new picture costs, or none by hand. Pure rules, no React.
import {
  BRIEF_MAX,
  CHANNELS,
  isScheduleTime,
  type ScheduleTime,
  postTemplateSchema,
  shapeLabel,
  type Channel,
  type MediaMode,
  type PostFormat,
  type PostTemplate,
} from '@str/shared';

export type { ScheduleTime };
import { formatFor } from './composer-v2';

export type Kind = 'Post' | 'Reel';
/** The moment, then the photos (and what the shape needs), where Make it is. */
export type WizardStep = 1 | 2;
/** AI is the path; by hand is the quiet way out for a host who just wants to post what they have. */
export type Path = 'ai' | 'own';
export type Pick = { id: string; kind: 'photo' | 'video' | 'audio'; url?: string | null };
/** What a shape asks the host for before it can be made. */
export type ShapeInput = {
  quote?: { text: string; author: string };
  dates?: { dates: string; price: string; link: string };
};

/** How a shape is drawn on the host's own photo in the picker. */
export type ShapePreview =
  'quote' | 'tour' | 'split' | 'spotlight' | 'offer' | 'guide' | 'behind' | 'plain';
export type Shape = {
  id: string;
  title: string;
  blurb: string;
  /** The brief handed to the generator; the host's optional line is appended. */
  brief: string;
  preview: ShapePreview;
  /** Something the host has to give first: a review to quote, or the dates to promote. */
  needs?: 'quote' | 'dates';
};

export const SHAPES: Shape[] = [
  {
    id: 'guest-quote',
    title: 'Guest quote',
    blurb: 'A review, big, on your calmest photo.',
    brief:
      'A guest review, quoted big on the calmest photo, then the room they mention, then where to book',
    preview: 'quote',
    needs: 'quote',
  },
  {
    id: 'room-tour',
    title: 'Room tour',
    blurb: 'One room a photo, best one last.',
    brief:
      'A room tour: one room per picture with its name on it, the best room last, and a caption that walks through the place',
    preview: 'tour',
  },
  {
    id: 'before-after',
    title: 'Before / after',
    blurb: 'Morning, then dusk.',
    brief:
      'Before and after: the same place in the morning and at dusk, a hook on the first picture and the reveal on the second',
    preview: 'split',
  },
  {
    id: 'amenity',
    title: 'Amenity spotlight',
    blurb: 'One thing guests love, named on the picture.',
    brief:
      'An amenity spotlight: the one thing guests love most, named big on its photo, and why it matters in the caption',
    preview: 'spotlight',
  },
  {
    id: 'promote-dates',
    title: 'Promote dates',
    blurb: 'Open nights and the price on your best photo.',
    brief:
      'Promote open dates: the nights still open and the price on the best photo, the offer in the first six words of the caption',
    preview: 'offer',
    needs: 'dates',
  },
  {
    id: 'area-guide',
    title: 'Area guide',
    blurb: 'Three stops nearby, named on your photo.',
    brief:
      'An area guide: up to three places to go nearby named on the picture, each with how far it is from the door only where the listing or the owner says so (miles stay miles, never a made-up drive time), on a photo of the surroundings',
    preview: 'guide',
  },
  {
    id: 'behind',
    title: 'Behind the scenes',
    blurb: 'How you get it ready.',
    brief:
      'Behind the scenes: how the place is made ready for guests, one honest detail on the picture and the story in the caption',
    preview: 'behind',
  },
  {
    id: 'caption',
    title: 'Clean picture',
    blurb: 'A new picture with nothing written on it. The words go in the caption.',
    brief:
      'The photos as they are with nothing written on them, and a warm caption in the host’s voice with one detail guests mention and the booking line',
    preview: 'plain',
  },
  {
    id: 'idea',
    title: 'Your idea',
    blurb: 'An image and caption that tell your story.',
    brief:
      'Create one image of the requested subject and a matching social caption. Follow the user idea; do not invent amenities, travel times or verified facts about named places.',
    preview: 'plain',
  },
];

/** We choose for the host: the first three usable listing photos, in library order. */
export function suggestPicks(lib: Pick[], n = 3): string[] {
  return lib
    .filter((m) => m.kind === 'photo' && !!m.url)
    .slice(0, n)
    .map((m) => m.id);
}

/** The shapes whose words are the post: the blurb promises them any good photo of the place. */
const TAKES_ANY_PHOTO = new Set(['area-guide', 'guest-quote', 'promote-dates']);
export const shapeTakesAnyPhoto = (shape: Shape) => TAKES_ANY_PHOTO.has(shape.id);

const OUTSIDE = /\b(?:exterior|outside|front|aerial|drone|facade)\b/i;
const VIEW =
  /\b(?:views?|mountains?|lake|ocean|beach|river|scenery|landscape|surroundings|forest|deck|porch|balcony|patio|yard)\b/i;

/** The photo words can stand on when none matched them: the outside, a view, else the lead photo. */
export function backdropPhoto<T extends { kind: string; label: string; url?: string | null }>(
  lib: readonly T[],
): T | null {
  const usable = lib.filter((m) => m.kind === 'photo' && !!m.url);
  return (
    usable.find((m) => OUTSIDE.test(m.label)) ??
    usable.find((m) => VIEW.test(m.label)) ??
    usable[0] ??
    null
  );
}

/** Morning, then dusk: one spot, drawn twice, so it starts from one photo. */
const isOneSpot = (shape?: Shape) => shape?.id === 'before-after';

/** How many of the matched photos a shape starts from. */
export function photosOffered(shape: Shape): number {
  return isOneSpot(shape) ? 1 : 3;
}

/**
 * A post takes up to ten pictures; a reel one video, or up to six photos as the place (none: we
 * pick). The AI road's Before / after takes the one spot it promises.
 */
export function pickLimit(kind: Kind, path: Path = 'own', shape?: Shape): number {
  if (kind === 'Reel') return 6;
  return path === 'ai' && isOneSpot(shape) ? 1 : 10;
}

/** Tapping adds at the end (that is the order), tapping again removes; with room for one it swaps. */
export function togglePick(picked: string[], id: string, limit: number): string[] {
  if (picked.includes(id)) return picked.filter((x) => x !== id);
  if (limit === 1) return [id];
  return picked.length >= limit ? picked : [...picked, id];
}

/**
 * What the road makes of the photos. Describing the moment matches the host's photos to the
 * words and makes a completely new picture of the same place from them (the season can turn, a
 * meal can appear on the deck): that is `reimagine`, and it costs credits. By hand the photos
 * are the post as they are, free.
 */
export function mediaModeFor(path: Path, _shapeId?: string): MediaMode {
  // Only the by-hand road posts the photos as they are; a shape never turns the AI off.
  return path === 'own' ? 'photos' : 'reimagine';
}

/** What Make it makes, in the price list's words: said beside the button, before the click. */
export function pictureMadeBy(mode: MediaMode): string {
  switch (mode) {
    case 'photos':
      return 'Your photos, no AI';
    case 'enhance':
      return 'Enhanced by AI from your photos';
    case 'reimagine':
      return 'Reimagined by AI from your photos';
    case 'imagine':
      return 'Imagined by AI from your words';
  }
}

/**
 * What the post is. By hand, what the host taps is the post. On the AI road the shape decides:
 * the matched photos are references for one new picture; a tour is a carousel of a card per photo,
 * never more cards than photos; a before and after is always its two cards of the one spot.
 */
export function wizardFormat(
  kind: Kind,
  picks: Pick[],
  path: Path = 'own',
  shape?: Shape,
): PostFormat {
  if (kind === 'Reel') return 'Reel';
  const media = picks
    .filter((p) => p.kind !== 'audio')
    .map((p) => ({ kind: p.kind as 'photo' | 'video' }));
  if (path === 'own') return formatFor(media);
  if (isOneSpot(shape)) return 'Carousel';
  return shape?.id === 'room-tour' && photosOnly(picks).length >= 2 ? 'Carousel' : 'Image';
}

const photosOnly = (picks: Pick[]) => picks.filter((p) => p.kind === 'photo');
const oneVideo = (picks: Pick[]) => picks.length === 1 && picks[0]!.kind === 'video';

/** What the field says under an idea longer than the API takes, or null while it fits. */
export function lineTooLong(line: string): string | null {
  if (line.length <= BRIEF_MAX) return null;
  return `Keep the idea under ${BRIEF_MAX.toLocaleString('en-US')} characters (currently ${line.length.toLocaleString('en-US')}).`;
}

/** What a shape still asks for, in the host's words; nothing for the shapes that need nothing. */
export function needsLine(shape: Shape): string | null {
  if (shape.needs === 'quote') return 'Needs: a review';
  if (shape.needs === 'dates') return 'Needs: dates, price, booking link';
  return null;
}

export function shapeReady(shape: Shape, input: ShapeInput): boolean {
  if (shape.needs === 'quote') return !!input.quote?.text.trim();
  if (shape.needs === 'dates') {
    const d = input.dates;
    return !!d && !!d.dates.trim() && !!d.price.trim() && !!d.link.trim();
  }
  return true;
}

export function canGoOn(
  step: WizardStep,
  kind: Kind,
  picks: Pick[],
  shape?: Shape | null,
  ctx: { path?: Path; line?: string; input?: ShapeInput; canImagine?: boolean } = {},
): boolean {
  const path = ctx.path ?? 'ai';
  // With AI the words are the whole ask; by hand the photos are, so the moment can stay empty.
  // Longer than the API takes is not an ask either: the field says so, the button waits.
  if (step === 1) return path === 'own' || (!!ctx.line?.trim() && !lineTooLong(ctx.line));
  // With AI nothing chosen is fine: the generator imagines the moment or picks the photos itself.
  // A plan without Imagine has to start a post from a photo; a reel never Imagines.
  // By hand what you tap is the post: a post wants a photo, a reel wants exactly one video.
  const ok =
    path === 'ai'
      ? kind === 'Reel' || ctx.canImagine !== false || photosOnly(picks).length >= 1
      : kind === 'Post'
        ? photosOnly(picks).length >= 1
        : oneVideo(picks);
  // What a shape needs feeds the generator; by hand nothing is generated, so nothing is needed.
  return ok && (path === 'own' || !shape || shapeReady(shape, ctx.input ?? {}));
}

export function pickSummary(kind: Kind, picks: Pick[], path: Path = 'ai'): string {
  if (kind === 'Post') {
    const n = photosOnly(picks).length;
    if (n === 0) return 'Pick at least one';
    const what = n === 1 ? '1 chosen · a picture' : `${n} chosen · a carousel`;
    return path === 'own' ? `${what} · free` : what;
  }
  if (oneVideo(picks)) return '1 video · your reel';
  if (path === 'own') return 'Pick a video';
  const n = photosOnly(picks).length;
  if (n === 0) return 'No photos chosen · we pick the ones that fit';
  return `${n} ${n === 1 ? 'photo' : 'photos'} · the place in your reel`;
}

const MONTH =
  '(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\\.?';
const DAY = '\\d{1,2}(?:st|nd|rd|th)?\\b';
const DATES = new RegExp(
  `\\b${MONTH}\\s+${DAY}(?:\\s*(?:-|–|—|to|through)\\s*(?:${MONTH}\\s+)?${DAY})?`,
  'i',
);
const PRICE = /(?:\bfrom\s+)?\$\s?\d[\d,]*(?:\.\d{2})?(?:\s*(?:\/|per\s+|a\s+)\s*night)?/i;

/** The dates and the price the host already wrote in their words; empty for what is not there. */
export function datesFromLine(line: string): { dates: string; price: string } {
  return { dates: DATES.exec(line)?.[0].trim() ?? '', price: PRICE.exec(line)?.[0].trim() ?? '' };
}

/** What the dates form starts with: what the host typed stays, the gaps come from their words. */
export function fillDates(
  current: ShapeInput['dates'],
  line: string,
  bookingUrl: string | null,
): NonNullable<ShapeInput['dates']> {
  const found = datesFromLine(line);
  return {
    dates: current?.dates.trim() ? current.dates : found.dates,
    price: current?.price.trim() ? current.price : found.price,
    link: current?.link.trim() ? current.link : (bookingUrl ?? ''),
  };
}

const MONTH_NAMES = 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec'.split(' ');

/** The example in the dates field: the first Friday to Sunday at least two weeks out. */
export function datesPlaceholder(now: Date): string {
  const friday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 14);
  friday.setDate(friday.getDate() + ((5 - friday.getDay() + 7) % 7));
  const sunday = new Date(friday.getFullYear(), friday.getMonth(), friday.getDate() + 2);
  const from = `${MONTH_NAMES[friday.getMonth()]} ${friday.getDate()}`;
  return friday.getMonth() === sunday.getMonth()
    ? `${from}–${sunday.getDate()}`
    : `${from} – ${MONTH_NAMES[sunday.getMonth()]} ${sunday.getDate()}`;
}

export function wizardBrief(shape: Shape, line: string, input: ShapeInput = {}): string {
  const parts = [shape.brief];
  if (shape.needs === 'quote' && input.quote?.text.trim()) {
    const by = input.quote.author.trim();
    parts.push(
      `Quote this review word for word: “${input.quote.text.trim()}”${by ? ` — ${by}` : ''}`,
    );
  }
  if (shape.needs === 'dates' && input.dates) {
    const d = input.dates;
    parts.push(
      `The open dates: ${d.dates.trim()}. The price: ${d.price.trim()}. The booking link: ${d.link.trim()}`,
    );
  }
  const extra = line.trim();
  if (extra)
    parts.push(
      `Primary request (follow this over the template; photos are visual references, not separate topics): ${extra}`,
    );
  return parts.join('. ');
}

/* ------------------------------------------------- following a post from Inspiration */

/** Whose post Create follows: the handle, the small picture and the id, nothing else of theirs. */
export type Following = { handle: string; imageUrl: string | null; inspirationPostId: string };

/** The template's subject in words: what the strip says the post is about. */
const SUBJECT_WORDS: Partial<Record<PostTemplate['subjectFocus'], string>> = { other: 'the place' };

/** "Following @handle’s reel · Room tour · hot tub · cozy": the form being followed, in our words. */
export function followingLine(kind: Kind, template: PostTemplate, handle: string): string {
  const subject = SUBJECT_WORDS[template.subjectFocus] ?? template.subjectFocus.replace(/-/g, ' ');
  return [
    `Following @${handle}’s ${kind === 'Reel' ? 'reel' : 'post'}`,
    shapeLabel(template.shapeId),
    subject,
    template.mood[0],
  ]
    .filter(Boolean)
    .join(' · ');
}

function readFollowing(v: unknown): Following | null {
  if (!v || typeof v !== 'object') return null;
  const f = v as Partial<Following>;
  if (typeof f.handle !== 'string' || typeof f.inspirationPostId !== 'string') return null;
  return {
    handle: f.handle,
    imageUrl: typeof f.imageUrl === 'string' ? f.imageUrl : null,
    inspirationPostId: f.inspirationPostId,
  };
}

/* ------------------------------------------------- kept on this device until Make it */

export type WizardState = {
  kind: Kind;
  path: Path;
  line: string;
  picks: string[];
  shapeId: string;
  input: ShapeInput;
  /** The form of an Inspiration post to follow; its shapeId is the shape. */
  template?: PostTemplate | null;
  following?: Following | null;
  /** A reel's length and look, chosen on the first screen. */
  seconds?: 15 | 30;
  fidelity?: 'original' | 'balanced' | 'enhanced';
};

const key = (propertyId: string) => `str.wizard.${propertyId}`;

/** A half-finished wizard is not a post yet; it is kept in this browser so leaving loses nothing. */
export function saveWizard(propertyId: string, state: WizardState | null): void {
  try {
    if (state) localStorage.setItem(key(propertyId), JSON.stringify(state));
    else localStorage.removeItem(key(propertyId));
  } catch {
    /* private mode, full storage: the wizard still works, it just is not kept */
  }
}

export function loadWizard(propertyId: string): WizardState | null {
  try {
    const raw = localStorage.getItem(key(propertyId));
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<WizardState>;
    if (
      (v.kind !== 'Post' && v.kind !== 'Reel') ||
      (v.path !== 'ai' && v.path !== 'own') ||
      typeof v.line !== 'string' ||
      !Array.isArray(v.picks) ||
      typeof v.shapeId !== 'string'
    )
      return null;
    // A template that fails its closed sets is dropped, not the whole wizard; states saved
    // before there were templates load as they did.
    const template = v.template ? postTemplateSchema.safeParse(v.template) : null;
    const following = readFollowing(v.following);
    return {
      kind: v.kind,
      path: v.path,
      // A line saved longer than the API takes (an older, uncapped field) comes back trimmed.
      line: v.line.slice(0, BRIEF_MAX),
      picks: v.picks.filter((x): x is string => typeof x === 'string'),
      shapeId: v.shapeId,
      input: v.input && typeof v.input === 'object' ? v.input : {},
      ...(template?.success ? { template: template.data } : {}),
      ...(following ? { following } : {}),
      ...(v.seconds === 15 || v.seconds === 30 ? { seconds: v.seconds } : {}),
      ...(v.fidelity === 'original' || v.fidelity === 'balanced' || v.fidelity === 'enhanced'
        ? { fidelity: v.fidelity }
        : {}),
    };
  } catch {
    return null;
  }
}

/* ------------------------------------------------- what the next post keeps from the last */

export type NextPost = { time: ScheduleTime; channels: Channel[] };
const nextKey = (propertyId: string) => `str.next.${propertyId}`;

/** Make another like this keeps the slot and the channels too; the share sheet opens on them. */
export function saveNext(propertyId: string, next: NextPost | null): void {
  try {
    if (next) localStorage.setItem(nextKey(propertyId), JSON.stringify(next));
    else localStorage.removeItem(nextKey(propertyId));
  } catch {
    /* private mode */
  }
}

export function loadNext(propertyId: string): NextPost | null {
  try {
    const raw = localStorage.getItem(nextKey(propertyId));
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<NextPost>;
    if (!isScheduleTime(v.time) || !Array.isArray(v.channels)) return null;
    return {
      time: v.time,
      channels: v.channels.filter((c): c is Channel => CHANNELS.includes(c)),
    };
  } catch {
    return null;
  }
}
